import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Form, Splitter, Flex, TreeProps } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { findNode, mapTreeData } from '../utils/treeUtils';
import PageDetails from '../components/kioskdesigner/pagedetails';
import { useAuth } from '../auth/authcontext';
import { getPageProperties, getAllPagesForPageVersionDesigner } from '../services/pageservice';
import { useAvailablePageVersions } from '../hooks/kioskdesigner/useavailablepageversions';
import { useTreeActions } from '../hooks/kioskdesigner/usetreeactions';
import { useTreeTitleRenderer } from '../hooks/kioskdesigner/usetreetitlerenderer';
import { useTreeDropHandler } from '../hooks/kioskdesigner/usetreedrophandler';
import { usePageDetailsForm } from '../hooks/kioskdesigner/usepagedetailsform';
import { usePageVersionActions } from '../hooks/kioskdesigner/usepageversionactions';
import PageVersionActionBar from '../components/kioskdesigner/pageversionactionbar';
import PageTreePanel from '../components/kioskdesigner/pagetreepanel';
import PageContentPanel from '../components/kioskdesigner/pagecontentpanel';
import ModalsAndDrawers from '../components/kioskdesigner/modalsanddrawers';
import { splitterStyles } from './kioskpageversion.styles';
import { useModalHandlers } from '../hooks/kioskdesigner/usemodalhandlers';
import { TreeNode } from '../types/types';
import type { DataNode, EventDataNode } from 'antd/es/tree';



interface KioskPageVersionProps {
    hasUnsavedChanges: boolean;
    handleUnsavedChanges: (unsaved: boolean) => void;
}

interface ContextMenuState {
    visible: boolean;
    x: number;
    y: number;
    node: TreeNode | null;
}

export const Kioskpageversion: React.FC<KioskPageVersionProps> = ({
    hasUnsavedChanges,
    handleUnsavedChanges,
}) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const currentPageVersionGuid = id ?? '';

    const [data, setData] = useState<TreeNode[]>([]);
    const [isEditable, setIsEditable] = useState(true);
    const [isPageVersionActive, setIsPageVersionActive] = useState(true);
    const [newNodeTitle, setNewNodeTitle] = useState('');
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [loadingTree, setLoading] = useState(false);
    const [loadingPageDetails, setLoadingPageDetails] = useState(false);
    const [loadingInitial, setLoadingInitial] = useState(false);
    const [form] = Form.useForm();
    const [selectedPageName, setSelectedPageName] = useState('');
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
        visible: false,
        x: 0,
        y: 0,
        node: null,
    });
    const [selectedView, setSelectedView] = useState<'editor' | 'pageview' | null>('pageview');
    const [error, setError] = useState<string | null>(null);
    const [pageVersionName, setPageVersionName] = useState('');
    const [menuItemImages, setMenuItemImagesOptions] = useState<any[]>([]);
    const [isMenuItem, setIsMenuItem] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const [isOverrideImageUrl, setIsOverrideImageUrl] = useState(false);
    const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState(false);
    const [pageVersionStatus, setPageVersionStatus] = useState('');
    const [isPublishDrawerVisible, setIsPublishDrawerVisible] = useState(false);
    const [hasPageVersionStatusChanged, setHasPageVersionStatusChanged] = useState(false);
    const [hasPageVersionNameChanged, setHasPageVersionNameChanged] = useState(false);
    const [draftForPublishedPageVersion, setDraftForPublishedPageVersion] = useState<string | null>(null);
    const [isStoresListVisible, setIsStoresListVisible] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [isEditPropDrawerVisible, setIsEditPropDrawerVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [navigationTargetKey, setNavigationTargetKey] = useState<string | null>(null);
    const [clickedOn, setClickedOn] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);

    const { userId } = useAuth();
    const userGuid = userId ?? null;

    const {
        handleOkModal,
        handleCancelModal,
        handleCreateNewVersionClick,
        handlePublishPageVersionClick,
        handleSimulatorClick,
        handleDuplicateClick,
        handleEditPageVersionClick,
        handleShowStoresClick,
    } = useModalHandlers({
        clickedOn,
        setClickedOn,
        navigationTargetKey,
        setNavigationTargetKey,
        setIsModalVisible,
        handleUnsavedChanges,
        hasUnsavedChanges,
        setSelectedKey,
        setSelectedPageName,
        setSelectedView,
        setIsCreateDrawerVisible,
        setIsPublishDrawerVisible,
        setDrawerOpen,
        setOpenEditModal,
        setIsStoresListVisible,
        data,
        currentPageVersionGuid,
    });

    const showEditForm = useCallback(async () => {
        if (selectedKey) {
            setLoadingPageDetails(true);
            setImageUrl('');
            const pageProperties = await getPageProperties(selectedKey);
            if (!pageProperties) {
                setLoadingPageDetails(false);
                return;
            }

            const isMenuItemSet = pageProperties.menuGuid !== null;
            setIsMenuItem(isMenuItemSet);
            setIsOverrideImageUrl(pageProperties.isCustomImage);

            if (pageProperties) {
                form.resetFields();
                form.setFieldsValue({
                    name: pageProperties.name,
                    isMenuItem: isMenuItemSet,
                    menuItem: pageProperties.menuGuid,
                    signatureItem: pageProperties.signatureItem,
                    addImage: pageProperties.isCustomImage,
                    selectedImageUrl: pageProperties.menuImageUrl,
                    isHeroItem: pageProperties.isHeroItem ?? false,
                    bannerImage: pageProperties.bannerImageUrl,
                    heroBadgeText: pageProperties.heroBadgeText || 'FEATURED',
                    heroBadgeStyle: pageProperties.heroBadgeStyle || 'orange',
                    startTime: pageProperties.startTime ? dayjs(`2000-01-01T${pageProperties.startTime}`) : null,
                    endTime: pageProperties.endTime ? dayjs(`2000-01-01T${pageProperties.endTime}`) : null,
                });

                setMenuItemImagesOptions(pageProperties.imagedropdownoptions);
                setImageUrl(pageProperties.menuImageUrl);
                setLoadingPageDetails(false);
            }
        }
    }, [selectedKey, form]);

    useEffect(() => {
        setLoadingInitial(true);
        const pageVersionGuid = id ?? '4080FCAA-11EA-4BD9-86A5-59DEE5C4CA99';

        getAllPagesForPageVersionDesigner(pageVersionGuid)
            .then((response) => {
                const transformedData = mapTreeData([response]);
                setData(transformedData);
                setPageVersionName(transformedData[0].pageVersionName);
                setPageVersionStatus(transformedData[0].pageVersionStatus);
                setIsPageVersionActive(transformedData[0].active === true);
                const isPageVersionEditable = transformedData[0].pageVersionStatus === 'draft' && transformedData[0].active === true;
                setIsEditable(isPageVersionEditable);
                setDraftForPublishedPageVersion(transformedData[0].draftForPublishedPageVersion);
                if (transformedData.length > 0) {
                    setSelectedKey(transformedData[0].key);
                    setSelectedPageName(transformedData[0].title);
                }
            })
            .catch(() => {
                setError('Failed to load page data. Please try again.');
            })
            .finally(() => {
                setLoadingInitial(false);
            });
    }, [id, hasPageVersionStatusChanged, hasPageVersionNameChanged]);

    const { availablePageVersions, setAvailablePageVersions } = useAvailablePageVersions({
        currentPageVersionGuid,
        hasPageVersionStatusChanged,
        hasPageVersionNameChanged,
    });

    useEffect(() => {
        if (selectedView === 'editor' && selectedKey) {
            showEditForm();
        }
    }, [selectedKey, selectedView, showEditForm]);

    const handlePageVersionChange = (value: string) => {
        if (hasUnsavedChanges) {
            setNavigationTargetKey(value);
            setIsModalVisible(true);
            setClickedOn('pageversionchange');
        } else {
            navigate(`/kioskpageversion/${value}`);
        }
    };

    const getRootNodeKey = useCallback(() => {
        return data.length > 0 ? data[0].key : null;
    }, [data]);

    const { addNode, removeNode } = useTreeActions({ id: id ?? '', setLoading });

    const handleAddNode = () => {
        if (!selectedKey || !newNodeTitle) return;
        addNode({ selectedKey, newNodeTitle, data, setData, setNewNodeTitle, createdByGuid: userGuid ?? '' });
    };

    const handleRemoveNode = (nodeKeyToRemove: string, nodeTitle: string) => {
        removeNode({
            nodeKeyToRemove,
            nodeTitle,
            data,
            setData,
            setSelectedKey,
            setSelectedPageName,
            setSelectedView,
            getRootNodeKey,
        });
    };

    const renderTreeTitle = useTreeTitleRenderer({ isEditable, data, handleRemoveNode });
    const { onDrop } = useTreeDropHandler({ data, setData, setLoading });

    const handleRightClick = ({
        event,
        node,
    }: {
        event: React.MouseEvent<Element, MouseEvent>;
        node: EventDataNode<DataNode>;
    }) => {
        event.preventDefault();

        const treeNodeData = (node as any) as TreeNode;

        if (!treeNodeData) {
            console.warn('Missing TreeNode data on right-click');
            return;
        }

        setContextMenu({
            visible: true,
            x: event.clientX,
            y: event.clientY,
            node: treeNodeData,
        });
    };


    const handleMenuClick = ({ key }: { key: string }) => {
        const selectedKey = contextMenu.node?.key ?? null;
        setSelectedKey(selectedKey);
        if (selectedKey) {
            const selectedNode = findNode(data, selectedKey);
            if (selectedNode) {
                setSelectedPageName(selectedNode.title);
            }
        }
        setSelectedView(key === 'editor' ? 'editor' : 'pageview');
        setContextMenu({ ...contextMenu, visible: false });
    };

    const {
        handleOk,
        handleDropdownSelection,
        handleMenuItemChange,
        handleImageUrlChange,
        handleInputChange,
    } = usePageDetailsForm({
        form,
        data,
        setData,
        selectedKey,
        setSelectedPageName,
        menuItemImages,
        setMenuItemImagesOptions,
        imageUrl,
        setImageUrl,
        isOverrideImageUrl,
        setIsOverrideImageUrl,
        setIsMenuItem,
        setLoadingPageDetails,
        handleUnsavedChanges,
    });

    const content = (
        <PageDetails
            form={form}
            menuItemImages={menuItemImages}
            handleDropdownSelection={handleDropdownSelection}
            isMenuItem={isMenuItem}
            handleMenuItemChange={handleMenuItemChange}
            imageUrl={imageUrl}
            isOverrideImageUrl={isOverrideImageUrl}
            handleImageUrlChange={handleImageUrlChange}
            handleInputChange={handleInputChange}
            handleOk={handleOk}
            isEditable={isEditable}
            onUnsavedChanges={handleUnsavedChanges}
            loading={loadingPageDetails}
            pageGuid={selectedKey ?? ''}
            pageversionGuid={currentPageVersionGuid}
        />
    );

    const {
        handleCreateNewVersion,
        handleSendForApproval,
        handleEditPageVersionProperties,
        updatePageVersionStatusToDraft,
    } = usePageVersionActions({
        userGuid: userGuid ?? '',
        id: id ?? '',
        pageVersionName,
        setPageVersionName,
        setAvailablePageVersions,
        setHasPageVersionStatusChanged,
        setHasPageVersionNameChanged,
        setLoadingInitial,
        setIsCreateDrawerVisible,
        setIsPublishDrawerVisible,
        setIsEditPropDrawerVisible,
    });

    const handleGoToDraft = () => {
        setOpenEditModal(false);
        navigate(`/kioskpageversion/${draftForPublishedPageVersion}`);
    };

    const onSelect = (
        selectedKeys: React.Key[],
        info: {
            event: 'select';
            selected: boolean;
            node: EventDataNode<DataNode>;
            selectedNodes: DataNode[];
            nativeEvent: MouseEvent;
        }
    ) => {
        const newSelectedKey = String(selectedKeys[0] ?? selectedKey);

        if (newSelectedKey === selectedKey) {
            setSelectedView('editor');
            return;
        }

        if (hasUnsavedChanges) {
            setNavigationTargetKey(newSelectedKey);
            setIsModalVisible(true);
            setClickedOn('page');
        } else {
            setSelectedKey(newSelectedKey);
            const selectedNode = findNode(data, newSelectedKey);
            if (selectedNode) {
                setSelectedPageName(selectedNode.title);
            }
            setSelectedView('editor');
        }
    };


    return (
        <Flex flex={1} style={{ height: 'calc(100vh - 17.5vh)', display: 'flex', flexDirection: 'column' }}>
            {error && (
                <Alert
                    closable
                    message={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: '10px' }}
                />
            )}

            <PageVersionActionBar
                loading={loadingInitial}
                pageVersionName={pageVersionName}
                availablePageVersions={availablePageVersions}
                pageVersionStatus={pageVersionStatus}
                isPageVersionActive={isPageVersionActive}
                onChange={handlePageVersionChange}
                onCreate={handleCreateNewVersionClick}
                onPublish={handlePublishPageVersionClick}
                onEdit={handleEditPageVersionClick}
                onDuplicate={handleDuplicateClick}
                onSimulator={handleSimulatorClick}
                onShowStores={handleShowStoresClick}
            />

            <Splitter style={splitterStyles.container}>
                <Splitter.Panel defaultSize="25%" min="15%" max="50%" style={splitterStyles.leftPanel}>
                    <PageTreePanel
                        loadingInitial={loadingInitial}
                        loadingTree={loadingTree}
                        data={data}
                        getRootNodeKey={() => getRootNodeKey() ?? ''}
                        handleRightClick={handleRightClick}
                        onSelect={onSelect}
                        onDrop={onDrop}
                        renderTreeTitle={renderTreeTitle}
                        isEditable={isEditable}
                        contextMenu={contextMenu}
                        setContextMenu={({ visible, x, y }) => {
                            setContextMenu(prev => ({
                                ...prev,
                                visible,
                                x,
                                y,
                            }));
                        }}
                        onMenuClick={handleMenuClick}
                    />
                </Splitter.Panel>

                <Splitter.Panel style={splitterStyles.rightPanel}>
                    <PageContentPanel
                        selectedKey={selectedKey}
                        selectedView={selectedView}
                        selectedPageName={selectedPageName}
                        loadingInitial={loadingInitial}
                        loadingPageDetails={loadingPageDetails}
                        newNodeTitle={newNodeTitle}
                        setNewNodeTitle={setNewNodeTitle}
                        handleAddNode={handleAddNode}
                        content={content}
                        isEditable={isEditable}
                        data={data}
                    />
                </Splitter.Panel>
            </Splitter>

            <ModalsAndDrawers
                isCreateDrawerVisible={isCreateDrawerVisible}
                setIsCreateDrawerVisible={setIsCreateDrawerVisible}
                isPublishDrawerVisible={isPublishDrawerVisible}
                setIsPublishDrawerVisible={setIsPublishDrawerVisible}
                openEditModal={openEditModal}
                setOpenEditModal={setOpenEditModal}
                isEditPropDrawerVisible={isEditPropDrawerVisible}
                setIsEditPropDrawerVisible={setIsEditPropDrawerVisible}
                isModalVisible={isModalVisible}
                setIsModalVisible={setIsModalVisible}
                isStoresListVisible={isStoresListVisible}
                setIsStoresListVisible={setIsStoresListVisible}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                pageVersionName={pageVersionName}
                currentPageVersionGuid={currentPageVersionGuid}
                draftForPublishedPageVersion={draftForPublishedPageVersion}
                handleCreateNewVersion={handleCreateNewVersion}
                handleSendForApproval={handleSendForApproval}
                handleEditPageVersionProperties={handleEditPageVersionProperties}
                updatePageVersionStatusToDraft={updatePageVersionStatusToDraft}
                handleOkModal={handleOkModal}
                handleCancelModal={handleCancelModal}
                handleGotoDraft={handleGoToDraft}
            />
        </Flex>
    );
};